import Icon from '@components/common/Icon';
import { DATA_BASE_URL } from '@lib/constants';

const notebooks = [
  {
    title: 'Grade 9 Engineering Notebook',
    year: 'Grade 9',
    description: 'Initial foray into competitive robotics, documenting design iterations, build process, and early learning experiences.',
    file: `${DATA_BASE_URL}/data/robotics/notebooks/grade_9_engineering_notebook.pdf`,
    highlights: ['Foundation concepts', 'First competitions', 'Initial designs']
  },
  {
    title: 'Grade 10 Samosa Slappahs Design Notebook',
    year: 'Grade 10',
    description: 'Advanced design documentation for the Samosa Slappahs robot, featuring improved autonomous systems and strategic gameplay.',
    file: `${DATA_BASE_URL}/data/robotics/notebooks/grade_10_samosa_slappas_design_notebook.pdf`,
    highlights: ['Autonomous programming', 'Mechanical innovations', 'Tournament strategies']
  },
  {
    title: 'Grade 11 Abracadabra Notebook',
    year: 'Grade 11',
    description: 'Comprehensive engineering notebook showcasing leadership, complex systems integration, and competition-winning designs.',
    file: `${DATA_BASE_URL}/data/robotics/notebooks/grade_11_abracadabra_notebook.pdf`,
    highlights: ['Team leadership', 'System optimization', 'World Championship prep']
  }
];

const DesignNotebooksSection = () => {
  return (
    <section className="py-16 sm:py-20 px-4">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h2
            className="text-3xl sm:text-4xl font-heading font-bold mb-4 tracking-tight"
            style={{ color: 'var(--color-heading)' }}
          >
            Engineering Documentation
          </h2>
          <p
            className="text-lg max-w-2xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Comprehensive design notebooks documenting years of innovation
          </p>
        </div>

        {/* Horizontal scroll on mobile, grid on desktop */}
        <div className="md:grid md:grid-cols-3 md:gap-6 flex md:flex-none overflow-x-auto gap-6 pb-4 snap-x snap-mandatory scrollbar-hide">
          {notebooks.map((notebook, index) => (
            <div
              key={index}
              className="group rounded-xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-200 flex-shrink-0 w-[85vw] md:w-auto snap-center"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-border-light)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              {/* PDF Preview/Header */}
              <div
                className="relative h-48 flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, var(--color-accent-2), var(--color-accent-3))'
                }}
              >
                <div className="text-center">
                  <Icon name="robotics" size="2xl" style={{ color: 'var(--color-accent-7)' }} />
                  <div
                    className="mt-3 text-sm font-bold"
                    style={{ color: 'var(--color-accent-8)' }}
                  >
                    {notebook.year}
                  </div>
                </div>
                {/* Overlay on hover */}
                <div
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                >
                  <div className="text-center">
                    <Icon name="download" size="xl" className="text-white mb-2" />
                    <div className="text-white text-sm font-medium">View Notebook</div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3
                  className="text-xl font-bold mb-2"
                  style={{ color: 'var(--color-heading)' }}
                >
                  {notebook.title}
                </h3>
                <p
                  className="text-sm leading-relaxed mb-4"
                  style={{ color: 'var(--color-card-text)' }}
                >
                  {notebook.description}
                </p>

                {/* Highlights */}
                <ul className="space-y-1.5 mb-4">
                  {notebook.highlights.map((highlight, i) => (
                    <li
                      key={i}
                      className="flex items-start text-xs"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      <span className="mr-2 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-accent-6)' }}>
                        •
                      </span>
                      {highlight}
                    </li>
                  ))}
                </ul>

                {/* View Button */}
                <a
                  href={notebook.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{
                    backgroundColor: 'var(--color-button-primary-bg)',
                    color: 'var(--color-button-primary-text)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-button-primary-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-button-primary-bg)';
                  }}
                >
                  <Icon name="download" size="sm" />
                  View Notebook
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Robotics Summary */}
        <div
          className="mt-8 rounded-xl p-6 text-center shadow-card"
          style={{
            backgroundColor: 'var(--color-card-bg)',
            borderColor: 'var(--color-border-light)',
            borderWidth: '1px',
            borderStyle: 'solid'
          }}
        >
          <div className="max-w-2xl mx-auto">
            <h3
              className="text-lg font-bold mb-2"
              style={{ color: 'var(--color-heading)' }}
            >
              Complete Robotics Summary
            </h3>
            <p
              className="text-sm mb-4"
              style={{ color: 'var(--color-muted)' }}
            >
              Comprehensive overview of all robotics achievements, competitions, and innovations
            </p>
            <a
              href={`${DATA_BASE_URL}/data/robotics/documents/robotics_summary.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
              style={{
                borderColor: 'var(--color-button-outline-border)',
                color: 'var(--color-button-outline-text)',
                borderWidth: '1.5px',
                borderStyle: 'solid',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-button-outline-hover-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Icon name="download" size="sm" />
              Download Summary
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DesignNotebooksSection;
